import type { BuildingGraphPlane } from "building-types/building/buildingGraphTypes";
import type { Building } from "building-types/building/buildingTypes";
import type { WallPlane } from "building-types/walls2/wallPlane";
import type { AABB2 } from "geom/aabb2Types";
import { planeCopy, planeEval } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polyLine2AABB2, polyLine2Centroid } from "geom/polyLine2";
import type { Vec3 } from "geom/vec3";

/** Resolves a building-graph plane by table key or `planeHash` field. */
export function buildingGraphPlaneForHash(building: Building, planeHash: string): BuildingGraphPlane | undefined {
  return (
    building.buildingGraph.buildingPlanes[planeHash] ??
    Object.values(building.buildingGraph.buildingPlanes).find((entry) => entry.planeHash === planeHash)
  );
}

/** Resolves a wall-plane entity by table key or `planeHash` field. */
export function wallPlaneEntityForHash(building: Building, planeHash: string): WallPlane | undefined {
  return (
    building.wallPlanes[planeHash] ?? Object.values(building.wallPlanes).find((entry) => entry.planeHash === planeHash)
  );
}

/** UV AABB of the wall plane outline loop, or null if the plane or loop is missing. */
export function wallPlaneOutlineAABB(building: Building | undefined, planeHash: string): AABB2 | null {
  const buildingPlane = building ? buildingGraphPlaneForHash(building, planeHash) : undefined;
  const outline = buildingPlane?.planeFace.loops[0];
  if (!outline) {
    return null;
  }
  return polyLine2AABB2(outline);
}

/** World-space centroid of the wall plane outline, or null if unavailable. */
export function wallPlaneOutlineCentroidWorld(building: Building | undefined, planeHash: string): Vec3 | null {
  const buildingPlane = building ? buildingGraphPlaneForHash(building, planeHash) : undefined;
  const outline = buildingPlane?.planeFace.loops[0];
  if (!outline || outline.length < 4) {
    return null;
  }
  const centroid = polyLine2Centroid(outline);
  if (!centroid) {
    return null;
  }
  const [worldX, worldY, worldZ] = planeEval(buildingPlane.planeFace.plane, centroid[0], centroid[1]);
  return [worldX, worldY, worldZ];
}

/** Reference plane for wall-plane UV editing (copy of the plane face). */
export function wallPlaneReferencePlane(building: Building | undefined, planeHash: string): Plane | null {
  const buildingPlane = building ? buildingGraphPlaneForHash(building, planeHash) : undefined;
  return buildingPlane ? planeCopy(buildingPlane.planeFace.plane) : null;
}
